import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { BlueprintChat, type BlueprintQuestion } from "@/components/blueprint/BlueprintChat";
import { useCreateGoal, useGoals, useSaveGoalProfile } from "@/lib/queries";
import { TIER_2_OPENER, splitList, useBlueprintSave } from "@/lib/blueprint";

/**
 * 7d — the learning blueprint.
 *
 * Fills `learning_profiles`, and it is the one track where the answers do all
 * the work. The other three rank against the curated base's `goalFit` scores;
 * learning has no buyer ladder to sort by, so what she says here — the topics,
 * the format, the size of room she can stand — *is* the ranking. That is why
 * this set asks about the shape of the room and the others do not.
 */
export const Route = createFileRoute("/learning")({
  validateSearch: z.object({ tier: z.literal(2).optional() }),
  component: Learning,
});

/**
 * Three questions, not five.
 *
 * The conversation is the gate on the feed now, so its length is the price of
 * seeing the rooms — and a five-question price on a screen she reached by
 * tapping "unlock" is where she closes the tab. Three she finishes.
 *
 * The three that stayed are the three the ranking reads. What went: `skillsToDevelop` and `topicsOnRadar` — the first restates the interests question in other words, the second is a wishlist the feed cannot act on until the base covers it.
 */
const TIER_1: BlueprintQuestion[] = [
  {
    field: "interests",
    ask: "What are you trying to get better at right now?",
    hint: "The thing you would give up an evening for.",
    placeholder: "Pricing, and how enterprise deals actually close…",
  },
  {
    field: "preferredEventTypes",
    ask: "How do you actually learn? Workshops, talks, small peer groups, conferences, online.",
    hint: "Name the ones you would go back to.",
    placeholder: "Workshops and small peer groups…",
  },
  {
    field: "eventSizePreference",
    ask: "Last one. How big a room?",
    choices: [
      { value: "intimate", label: "Under twenty" },
      { value: "medium", label: "Twenty to a hundred" },
      { value: "large", label: "Big rooms" },
    ],
    placeholder: "Pick one above",
  },
];

/**
 * Tier two — the constraints, which on this track matter more than the topic.
 *
 * A perfect workshop ninety minutes away on a Tuesday is not a match, and no
 * amount of subject-matter scoring catches that. `locationRadiusKm` is the one
 * typed column tier one left empty; the rest land in `extra` until one earns a
 * filter of its own.
 */
const TIER_2: BlueprintQuestion[] = [
  {
    field: "locationRadiusKm",
    ask: "How far will you actually go on a weeknight?",
    choices: [
      { value: "5", label: "Walking distance" },
      { value: "25", label: "Across the city" },
      { value: "500", label: "I will travel" },
    ],
    placeholder: "Pick one above",
  },
  {
    field: "eveningsPerMonth",
    ask: "How many evenings a month can you honestly give this?",
    hint: "The true number, not the aspirational one. It decides how much Omina shows you.",
    placeholder: "Two…",
  },
  {
    field: "wasWorthIt",
    ask: "What is the last thing you went to that was genuinely worth it? What made it good?",
    hint: "The closest thing to a specification for the next one.",
    placeholder: "A twelve-person dinner where everyone had shipped something…",
  },
  {
    field: "peopleWanted",
    ask: "Who do you want to be in the room with? Roles, stage, or names.",
    hint: "On this track the people are often the lesson.",
    placeholder: "Founders two years ahead of me…",
  },
  {
    field: "wouldLeaveEarly",
    ask: "Last one. What makes you leave early?",
    hint: "Naming it is what lets Omina filter it out rather than rank it low.",
    placeholder: "Panels with four people and no argument…",
  },
];

function Learning() {
  const { tier } = Route.useSearch();
  const isDeep = tier === 2;

  const goals = useGoals();
  const createGoal = useCreateGoal();
  const saveGoalProfile = useSaveGoalProfile();
  const navigate = useNavigate();
  const save = useBlueprintSave({
    type: "learn",
    goals,
    createGoal,
    saveGoalProfile,
    /**
     * The feed, not the five.
     *
     * She has just spent a conversation describing exactly who she sells to, and
     * `/matches` answers that with three rooms and two behind a lock — the screen
     * whose job is to prove the product works to someone who has not paid, and
     * whose next control is a button back to the radar to scan again. Landing
     * there after the questions reads as being sent to the queue after doing the
     * work.
     *
     * The feed is what the answers actually bought: every room that ranks, in the
     * order the blueprint just changed.
     */
    onDone: () => void navigate({ to: "/feed" }),
  });

  return (
    <BlueprintChat
      title={isDeep ? "Sharpening your matches" : "Your learning blueprint"}
      opener={
        isDeep
          ? TIER_2_OPENER
          : "A few questions to unlock better matches, based on what you actually need. Answer them out loud or type — either is fine."
      }
      questions={isDeep ? TIER_2 : TIER_1}
      saving={save.saving}
      error={save.error}
      doneTitle="That is the blueprint."
      doneNote={
        isDeep
          ? "Omina now knows how far you will go and what makes you leave. Both are filters, not preferences."
          : "On this track your answers are the ranking — there is no buyer ladder to sort by, so rooms come back in date order against what you just said."
      }
      saveLabel={isDeep ? "Save and rescan" : "Save and see my rooms"}
      /* The feed either way — it is where both tiers are entered from now.
         Tier one used to return to `/matches`, which was true when the nudge
         lived there and is a detour now. */
      exitTo="/feed"
      onSave={(a) =>
        isDeep
          ? save.run({
              ...(a.locationRadiusKm
                ? { locationRadiusKm: Number(a.locationRadiusKm) }
                : {}),
              extra: {
                eveningsPerMonth: a.eveningsPerMonth,
                wasWorthIt: a.wasWorthIt,
                peopleWanted: a.peopleWanted,
                wouldLeaveEarly: a.wouldLeaveEarly,
              },
            })
          : save.run({
              ...(splitList(a.interests) ? { interests: splitList(a.interests) } : {}),
              ...(splitList(a.skillsToDevelop)
                ? { skillsToDevelop: splitList(a.skillsToDevelop) }
                : {}),
              ...(splitList(a.preferredEventTypes)
                ? { preferredEventTypes: splitList(a.preferredEventTypes) }
                : {}),
              ...(a.eventSizePreference ? { eventSizePreference: a.eventSizePreference } : {}),
              ...(splitList(a.topicsOnRadar)
                ? { topicsOnRadar: splitList(a.topicsOnRadar) }
                : {}),
            })
      }
    />
  );
}
